import type { Component, InjectionKey } from "vue";
import type { ConfirmOptions } from "../../composables/Dialog/confirm";
import type { DialogHeight, DialogSize } from "./dialogSizes";
import { computed, markRaw, reactive } from "vue";
import { i18n } from "../../translations/i18n";
import { resolveDialogHeight, resolveDialogSize, resolveDialogWidth } from "./dialogSizes";
import DirtyFieldsConfirmList from "./DirtyFieldsConfirmList.vue";

export type { ConfirmOptions };

export type { DialogHeight, DialogSize } from "./dialogSizes";
export { DIALOG_HEIGHTS, DIALOG_SIZES, resolveDialogHeight, resolveDialogSize, resolveDialogWidth } from "./dialogSizes";

export const DIALOG_STACK_KEY = Symbol("dialogStack");
export const DIALOG_LAYER_KEY: InjectionKey<DialogLayerApi> = Symbol("dialogLayer");

export const BASE_Z_INDEX = 1000;
export const SYSTEM_Z_INDEX = 20000;
export const Z_INDEX_INCREMENT = 2;
export const MAX_DIALOG_DEPTH = 3;

export type DialogVariant = "center" | "drawer";
export type DialogPriority = "normal" | "system";

export interface DialogRouteBinding {
  param: string
  value: string
}

export interface DialogLayerOptions {
  id?: string
  component?: Component
  props?: Record<string, unknown>
  variant?: DialogVariant
  size?: DialogSize | string
  /** Exact width in px; overrides the width of `size` but keeps its phone behaviour. */
  width?: number
  height?: DialogHeight | string
  priority?: DialogPriority
  route?: DialogRouteBinding
  dismissible?: boolean
  hideHostClose?: boolean
  /** A confirm that asks for a decision: rendered as an `alertdialog` with the confirm chrome. */
  alert?: boolean
  dirty?: () => boolean
  headless?: boolean
  paddingless?: boolean
  header?: string | null
  footer?: string | null
  mask?: boolean
  source?: "host" | "declarative"
  onClose?: () => void
  listeners?: Record<string, (...args: any[]) => void>
}

export interface DialogLayer {
  instanceId: string
  id: string
  component?: Component
  props: Record<string, unknown>
  variant: DialogVariant
  size: DialogSize
  width?: number
  height: DialogHeight
  priority: DialogPriority
  route?: DialogRouteBinding
  dismissible: boolean
  hideHostClose: boolean
  alert: boolean
  dirtyFlag: boolean
  /** Hard lock: blocks every non-forced pop (X, Escape, backdrop) while a write is in flight. */
  lockedFlag: boolean
  onLocked?: () => void
  dirtyFn?: () => boolean
  dirtyFieldLabelsFn?: () => DirtyFieldLabels
  headless: boolean
  paddingless: boolean
  header: string | null
  footer: string | null
  mask: boolean
  source: "host" | "declarative"
  zIndex: number
  parentInstanceId?: string
  historyOwned: boolean
  leaving: boolean
  onClose?: () => void
  listeners: Record<string, (...args: any[]) => void>
}

export interface DirtyFieldLabels {
  fields: string[]
  hasOther: boolean
}

export interface DialogLayerApi {
  id: string
  instanceId: string
  paddingless: boolean
  /** User dismiss. Asks to discard unsaved changes when the layer is dirty. */
  close: () => Promise<boolean>
  /** Dismiss after a successful write. Never asks to discard unsaved changes. */
  closeAfterSave: () => Promise<boolean>
  setDirty: (dirty: boolean) => void
  setDirtyFieldLabels: (fn: (() => DirtyFieldLabels) | undefined) => void
  setLocked: (locked: boolean) => void
  setOnLocked: (fn: (() => void) | undefined) => void
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

let instanceSeq = 0;
let dirtyNavigationSuppression = 0;

export function isDirtyNavigationSuppressed(): boolean {
  return dirtyNavigationSuppression > 0;
}

/** Route writes that replace a dialog must not ask to discard unsaved changes. */
export function beginDirtyNavigationSuppression(): () => void {
  dirtyNavigationSuppression += 1;
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    dirtyNavigationSuppression -= 1;
  };
}

function generateInstanceId(): string {
  instanceSeq += 1;
  return `dialog-${Date.now()}-${instanceSeq}`;
}

export function isLayerDirty(layer: DialogLayer): boolean {
  return layer.dirtyFlag || Boolean(layer.dirtyFn?.());
}

export function shouldRenderHostClose(layer: Pick<DialogLayer, "dismissible" | "hideHostClose">): boolean {
  return layer.dismissible && !layer.hideHostClose;
}

export function createDialogStack() {
  const layers = reactive<DialogLayer[]>([]);

  const top = computed(() => layers[layers.length - 1] ?? null);
  const topZIndex = computed(() => top.value?.zIndex ?? BASE_Z_INDEX - Z_INDEX_INCREMENT);
  const leaveWaiters = new Set<string>();
  const leaveResolvers = new Map<string, () => void>();
  const popInflight = new Map<string, Promise<boolean>>();

  const maskLayer = computed(() => {
    for (let i = layers.length - 1; i >= 0; i -= 1) {
      const layer = layers[i];
      if (layer?.mask && !layer.leaving) {
        return layer;
      }
    }
    return null;
  });
  const maskZIndex = computed(() => {
    const layer = maskLayer.value;
    return layer ? layer.zIndex - 1 : BASE_Z_INDEX - 1;
  });

  function computeZIndex(priority: DialogPriority): number {
    const baseline = priority === "system" ? SYSTEM_Z_INDEX : BASE_Z_INDEX;
    const max = layers
      .filter(layer => (priority === "system" ? layer.priority === "system" : layer.priority !== "system"))
      .reduce((current, layer) => Math.max(current, layer.zIndex), baseline - Z_INDEX_INCREMENT);
    return max + Z_INDEX_INCREMENT;
  }

  function push(options: DialogLayerOptions): string {
    if (options.id) {
      const existing = layers.find(layer => layer.id === options.id);
      if (existing) {
        bringToFront(existing.instanceId);
        if (options.props) {
          existing.props = options.props;
        }
        if (options.dirty) {
          existing.dirtyFn = options.dirty;
        }
        if (options.onClose) {
          existing.onClose = options.onClose;
        }
        if (options.route) {
          existing.route = options.route;
        }
        if (options.listeners) {
          existing.listeners = options.listeners;
        }
        return existing.instanceId;
      }
    }

    const parent = layers[layers.length - 1];
    const instanceId = generateInstanceId();
    const layer: DialogLayer = {
      instanceId,
      id: options.id ?? instanceId,
      component: options.component ? markRaw(options.component) : undefined,
      props: options.props ?? {},
      variant: options.variant ?? "center",
      size: resolveDialogSize(options.size),
      width: resolveDialogWidth(options.width),
      height: resolveDialogHeight(options.height),
      priority: options.priority ?? "normal",
      route: options.route,
      dismissible: options.dismissible ?? true,
      hideHostClose: options.hideHostClose ?? false,
      alert: options.alert ?? false,
      dirtyFlag: false,
      lockedFlag: false,
      dirtyFn: options.dirty,
      headless: options.headless ?? false,
      paddingless: options.paddingless ?? false,
      header: options.header ?? null,
      footer: options.footer ?? null,
      mask: options.mask ?? true,
      source: options.source ?? "host",
      zIndex: computeZIndex(options.priority ?? "normal"),
      parentInstanceId: parent?.instanceId,
      historyOwned: false,
      leaving: false,
      onClose: options.onClose,
      listeners: options.listeners ?? {},
    };
    layers.push(layer);
    return instanceId;
  }

  function bringToFront(instanceId: string): void {
    const index = layers.findIndex(layer => layer.instanceId === instanceId);
    if (index === -1 || index === layers.length - 1) {
      return;
    }
    const [layer] = layers.splice(index, 1);
    if (!layer) {
      return;
    }
    layer.zIndex = computeZIndex(layer.priority);
    layers.push(layer);
  }

  function get(instanceId: string): DialogLayer | undefined {
    return layers.find(layer => layer.instanceId === instanceId);
  }

  function getById(id: string): DialogLayer | undefined {
    return layers.find(layer => layer.id === id);
  }

  function registerLeaveWaiter(instanceId: string): void {
    leaveWaiters.add(instanceId);
  }

  function unregisterLeaveWaiter(instanceId: string): void {
    leaveWaiters.delete(instanceId);
  }

  function finishLeave(instanceId: string): void {
    const resolve = leaveResolvers.get(instanceId);
    if (!resolve) {
      return;
    }
    leaveResolvers.delete(instanceId);
    resolve();
  }

  function waitForLeave(instanceId: string): Promise<void> {
    if (!leaveWaiters.has(instanceId)) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      leaveResolvers.set(instanceId, resolve);
    });
  }

  function pop(instanceId?: string, options?: { force?: boolean, silent?: boolean }): Promise<boolean> {
    const target = instanceId
      ? layers.find(layer => layer.instanceId === instanceId)
      : layers[layers.length - 1];
    if (!target) {
      return Promise.resolve(true);
    }

    const existing = popInflight.get(target.instanceId);
    if (existing) {
      if (options?.force) {
        finishLeave(target.instanceId);
      }
      return existing;
    }

    const promise = (async (): Promise<boolean> => {
      if (!options?.force && target.lockedFlag) {
        target.onLocked?.();
        return false;
      }
      if (!options?.force && isLayerDirty(target)) {
        const confirmed = await requestDirtyConfirm(target);
        if (!confirmed) {
          return false;
        }
      }

      const children = layers.filter(layer => layer.parentInstanceId === target.instanceId);
      for (const child of children) {
        await pop(child.instanceId, { force: true });
      }

      if (!layers.some(layer => layer.instanceId === target.instanceId)) {
        return true;
      }

      if (!options?.force) {
        target.leaving = true;
        await waitForLeave(target.instanceId);
      } else {
        finishLeave(target.instanceId);
      }

      const index = layers.findIndex(layer => layer.instanceId === target.instanceId);
      if (index !== -1) {
        layers.splice(index, 1);
        if (!options?.silent) {
          target.onClose?.();
        }
      }
      return true;
    })();

    const tracked = promise.finally(() => {
      popInflight.delete(target.instanceId);
      leaveWaiters.delete(target.instanceId);
      leaveResolvers.delete(target.instanceId);
    });
    popInflight.set(target.instanceId, tracked);
    return tracked;
  }

  function popAfterSave(instanceId?: string, options?: { silent?: boolean }): Promise<boolean> {
    return pop(instanceId, { force: true, silent: options?.silent });
  }

  function replace(id: string, options: DialogLayerOptions, replaceOptions: { discardDirty?: boolean } = {}): string {
    const existing = layers.find(layer => layer.id === id || layer.id.startsWith(`${id}:`));
    if (existing) {
      // Only for a caller that already persisted the outgoing layer's state: the dirty flag is
      // dropped before the async pop so a route write in the same turn cannot ask to discard it.
      if (replaceOptions.discardDirty) {
        existing.dirtyFlag = false;
        existing.dirtyFn = undefined;
      }
      void pop(existing.instanceId, { force: true, silent: true });
    }
    return push({ ...options, id: options.id ?? id });
  }

  async function closeAll(): Promise<void> {
    while (layers.length > 0) {
      const last = layers[layers.length - 1];
      if (!last) {
        break;
      }
      await pop(last.instanceId, { force: true });
    }
  }

  function setDirty(instanceId: string, dirty: boolean): void {
    const layer = get(instanceId);
    if (layer) {
      layer.dirtyFlag = dirty;
    }
  }

  function setDirtyFieldLabels(instanceId: string, fn: (() => DirtyFieldLabels) | undefined): void {
    const layer = get(instanceId);
    if (layer) {
      layer.dirtyFieldLabelsFn = fn;
    }
  }

  function setLocked(instanceId: string, locked: boolean): void {
    const layer = get(instanceId);
    if (layer) {
      layer.lockedFlag = locked;
    }
  }

  function setOnLocked(instanceId: string, fn: (() => void) | undefined): void {
    const layer = get(instanceId);
    if (layer) {
      layer.onLocked = fn;
    }
  }

  function isLocked(instanceId: string): boolean {
    return get(instanceId)?.lockedFlag === true;
  }

  function anyDirty(): boolean {
    return layers.some(layer => isLayerDirty(layer));
  }

  function markHistoryOwned(instanceId: string): void {
    const layer = get(instanceId);
    if (layer) {
      layer.historyOwned = true;
    }
  }

  function isTop(instanceId: string): boolean {
    return top.value?.instanceId === instanceId;
  }

  return {
    layers,
    top,
    topZIndex,
    maskZIndex,
    maskLayer,
    push,
    pop,
    popAfterSave,
    replace,
    bringToFront,
    get,
    getById,
    closeAll,
    setDirty,
    setDirtyFieldLabels,
    setLocked,
    setOnLocked,
    isLocked,
    anyDirty,
    markHistoryOwned,
    isTop,
    registerLeaveWaiter,
    unregisterLeaveWaiter,
    finishLeave,
  };
}

export type DialogStack = ReturnType<typeof createDialogStack>;

let activeStack: DialogStack | null = null;
let confirmFn: ConfirmFn | null = null;

export function setActiveDialogStack(stack: DialogStack | null): void {
  activeStack = stack;
}

export function getDialogStack(): DialogStack {
  if (!activeStack) {
    throw new Error("Dialog stack is not installed. Call app.use(createDialogPlugin()).");
  }
  return activeStack;
}

export function tryGetDialogStack(): DialogStack | null {
  return activeStack;
}

export function registerStackConfirm(fn: ConfirmFn | null): void {
  confirmFn = fn;
}

function findTopDirtyLayer(): DialogLayer | undefined {
  if (!activeStack) {
    return undefined;
  }
  for (let i = activeStack.layers.length - 1; i >= 0; i -= 1) {
    const candidate = activeStack.layers[i];
    if (candidate && isLayerDirty(candidate)) {
      return candidate;
    }
  }
  return undefined;
}

export function collectDirtyFieldLabels(layer?: DialogLayer): DirtyFieldLabels {
  const summary = layer?.dirtyFieldLabelsFn?.();
  if (!summary) {
    return { fields: [], hasOther: false };
  }
  return {
    fields: summary.fields.filter(field => field.length > 0),
    hasOther: Boolean(summary.hasOther),
  };
}

export async function requestDirtyConfirm(layer?: DialogLayer): Promise<boolean> {
  if (!confirmFn) {
    return true;
  }
  const target = layer ?? findTopDirtyLayer();
  const summary = collectDirtyFieldLabels(target);
  function t(key: string): string {
    return String(i18n.global.t(key));
  }
  const showFields = summary.fields.length > 0;
  return confirmFn({
    title: t("base.dirty_check_title"),
    message: t("base.dirty_check_message"),
    variant: "warning",
    confirmText: t("base.dirty_check_confirm"),
    cancelText: t("base.dirty_check_cancel"),
    body: showFields ? markRaw(DirtyFieldsConfirmList) : undefined,
    bodyProps: showFields
      ? {
          fields: summary.fields,
          hasOther: summary.hasOther,
          heading: t("base.dirty_check_fields"),
          otherLabel: t("base.dirty_check_other"),
        }
      : undefined,
  });
}

export function resetDialogStack(): void {
  activeStack = null;
  confirmFn = null;
  instanceSeq = 0;
  dirtyNavigationSuppression = 0;
}
